import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useProducts } from "@/hooks/useProducts";
import { useNavigate } from "react-router-dom";
import { Sparkles } from "lucide-react";

const FeaturedProducts = () => {
  const navigate = useNavigate();
  const { data: allProducts, isLoading } = useProducts();
  const products = allProducts?.slice(0, 4);

  return (
    <section className="py-20 bg-gradient-to-b from-background to-cream/30 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-20 right-0 w-64 h-64 bg-gold/5 rounded-full blur-3xl animate-float" />
      <div className="absolute bottom-20 left-0 w-64 h-64 bg-accent/5 rounded-full blur-3xl animate-float" style={{ animationDelay: '1.5s' }} />
      
      <div className="container mx-auto px-4 relative">
        <div className="text-center mb-12 animate-slide-up">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-4">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-primary font-semibold text-sm">Selecție specială</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-bold text-primary mb-4">
            Produse recomandate
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto font-lora">
            Cele mai apreciate cărți din colecția noastră, create cu pasiune și măiestrie
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {isLoading ? (
            [1, 2, 3, 4].map((i) => (
              <Card key={i} className="overflow-hidden animate-fade-in" style={{ animationDelay: `${i * 100}ms` }}>
                <Skeleton className="h-64 w-full rounded-t-lg" />
                <CardContent className="p-6">
                  <Skeleton className="h-6 w-3/4 mb-2" />
                  <Skeleton className="h-8 w-24 mb-4" />
                  <Skeleton className="h-10 w-full" />
                </CardContent>
              </Card>
            ))
          ) : products && products.length > 0 ? (
            products.map((product, index) => (
              <Card
                key={product.id}
                className="group overflow-hidden hover:shadow-2xl transition-all duration-500 hover-lift cursor-pointer border-2 border-transparent hover:border-gold/30 glass-effect animate-fade-in relative"
                onClick={() => navigate(`/produs/${product.slug}`)}
                style={{ animationDelay: `${index * 150}ms` }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-gold/0 via-gold/5 to-gold/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                <div className="relative h-64 overflow-hidden bg-muted">
                  <img
                    src={product.product_images?.[0]?.src || "https://images.unsplash.com/photo-1543002588-bfa74002ed7e?q=80&w=600"}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-110 group-hover:rotate-1 transition-all duration-700"
                    onError={(e) => {
                      console.error("Image load error for:", product.name, product.product_images);
                      const target = e.target as HTMLImageElement;
                      target.src = "https://images.unsplash.com/photo-1543002588-bfa74002ed7e?q=80&w=600";
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-burgundy-dark/60 via-transparent to-gold/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-gold/0 via-gold/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                </div>
                <CardContent className="p-6 bg-gradient-to-b from-card to-cream/50 relative">
                  <h3 className="text-xl font-semibold text-foreground mb-2 line-clamp-2 group-hover:text-gradient-gold group-hover:bg-[length:200%_auto] group-hover:animate-gradient-shift transition-all font-playfair">
                    {product.name}
                  </h3>
                  <p className="text-2xl font-bold text-primary mb-4 group-hover:scale-105 transition-transform">
                    {product.price.toFixed(2)} RON
                  </p>
                  <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg hover-glow-gold group/btn relative overflow-hidden">
                    <span className="relative z-10">Vezi detalii</span>
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover/btn:translate-x-full transition-transform duration-700" />
                  </Button>
                </CardContent>
              </Card>
            ))
          ) : (
            <div className="col-span-full text-center py-12">
              <p className="text-muted-foreground text-lg mb-2">
                Nu există produse disponibile momentan.
              </p>
              <p className="text-sm text-muted-foreground font-lora">
                Produsele vor fi sincronizate automat din WooCommerce.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default FeaturedProducts;
